import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { getHierarchicalData } from 'assets/data/dataList';
import {
    Button,
    FieldErrorList,
    FormField,
    HierarchyList,
    Input,
    InputWithSearchIcon,
    Label,
    MessageModal,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Select,
    TextArea
} from 'common/components';
import {
    ACTION_ITEMS,
    CATEGORY_DESCRIPTION_MAX_LENGTH,
    SUB_CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR,
    SUB_CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR,
    SUB_CATEGORY_NAME_EMPTY_ERROR,
    SUB_CATEGORY_NAME_INVALID_CHAR_ERROR,
    SUB_CATEGORY_NAME_MAX_LENGTH,
    SUB_CATEGORY_NAME_MAX_LENGTH_ERROR,
    SUB_CATEGORY_ORDER_EMPTY_ERROR
} from 'common/config';
import {
    ButtonLabel,
    ButtonVariant,
    CategoryField,
    EntityType,
    InputType,
    MessageType
} from 'common/enums';
import { FieldError } from 'common/models';
import { getSortOrderList } from 'common/utils/data';
import { addValidationError, removeValidationError } from 'common/utils/error';
import { getHierarchyListColDefs } from 'modules/master-data/config';
import { masterDataActions } from 'modules/master-data/redux';

import './VariantAddAndMoveSubcategoryPopup.scss';
import SearchSelection from 'common/components/custom/search-list/SearchSelection';
import SearchList from 'common/components/custom/search-list/SearchList';
import { EntityVM, MasterDataEntity } from 'common/models/data/entity.model';
import { ADD_NEW_SUB_CATEGORY_BANNER_MESSAGE } from 'modules/configuration/config';

type VariantAddAndMoveSubcategoryPopupProps = {
    isVisible: boolean;
    fieldErrors: Array<FieldError>;
    updateFieldErrors: (errors: Array<FieldError>) => void;
    updateSelectedRadioButton: (id: string) => void;
    closePopupHandler: () => void;
    onBackButtonClicked: () => void;
    navigateToMasterData: () => void;
    entityId: string;
    entityType: EntityType;
    hierarchicalData: MasterDataEntity[];
    toggleExpansion: (payload: EntityVM) => void;
};
export default function VariantAddAndMoveSubcategoryPopup({
    isVisible,
    fieldErrors,
    updateFieldErrors,
    updateSelectedRadioButton,
    closePopupHandler,
    onBackButtonClicked,
    navigateToMasterData,
    entityId,
    hierarchicalData,
    toggleExpansion
}: VariantAddAndMoveSubcategoryPopupProps) {
    const [name, setName] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [parentId, setParentId] = useState<string>('');
    const [searchVal, setSearchVal] = useState<string>('');
    const [radioButtonForSearch, setRadioButtonForSearch] = useState<string>('');
    const [hideSearch, setHideSearch] = useState<boolean>(false);
    const [selectedRecord, setSelectedRecord] = useState<any>();

    const dispatch = useDispatch();

    const handleRadioClicked = (data: any) => {
        setParentId(data.id);
        updateSelectedRadioButton(data.id);
    };

    const handleDescription = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Description);
        const reg = new RegExp(/^[a-zA-Z0-9 ]*$/);
        if (event.target.value.length > CATEGORY_DESCRIPTION_MAX_LENGTH) {
            errors = addValidationError(
                errors,
                CategoryField.Description,
                SUB_CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR
            );
        }
        if (!reg.test(event.target.value)) {
            errors = addValidationError(
                errors,
                CategoryField.Description,
                SUB_CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR
            );
        }

        setDescription(event.target.value);
        updateFieldErrors(errors);
    };

    const handleNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Name);
        const reg = new RegExp(/^[a-zA-Z0-9 ]*$/);
        if (event.target.value.length > SUB_CATEGORY_NAME_MAX_LENGTH) {
            errors = addValidationError(
                errors,
                CategoryField.Name,
                SUB_CATEGORY_NAME_MAX_LENGTH_ERROR
            );
        }
        if (!reg.test(event.target.value)) {
            errors = addValidationError(
                errors,
                CategoryField.Name,
                SUB_CATEGORY_NAME_INVALID_CHAR_ERROR
            );
        }
        setName(event.target.value);
        updateFieldErrors(errors);
    };

    const handleSortOrderChange = (value: string) => {
        const errors = removeValidationError(fieldErrors, CategoryField.SortOrder);
        updateFieldErrors(errors);
        setSortOrder(value);
    };

    const sortOrderList = getSortOrderList(getHierarchicalData().length);
    const [isShowPopup, setShowPopup] = useState(false);

    const saveSubCategoryHandler = () => {
        let errors = fieldErrors;
        if (!name.trim()) {
            errors = addValidationError(errors, CategoryField.Name, SUB_CATEGORY_NAME_EMPTY_ERROR);
        }
        if (!sortOrder) {
            errors = addValidationError(
                errors,
                CategoryField.SortOrder,
                SUB_CATEGORY_ORDER_EMPTY_ERROR
            );
        }
        if (errors.length === 0) {
            if (parseInt(parentId) > -1) {
                dispatch(
                    masterDataActions.addSubCategory({
                        categoryId: parentId,
                        parentHierarchyId: '',
                        parentSubCategoryId: '',
                        subCategoryName: name,
                        order: Number(sortOrder),
                        description: description,
                        subCategoryLevel: 0
                    })
                );
                closePopupHandler();
                navigateToMasterData();
            } else {
                /**
                 * Error messsage should go here
                 */
                setShowPopup(true);
            }
        }
        updateFieldErrors(errors);
    };
    const onSearchChangeHandler = (event: any) => {
        setSearchVal(event.target.value);
    };

    const handleChangeForSearch = (event: React.ChangeEvent<HTMLInputElement>, list: any) => {
        setRadioButtonForSearch(event.target.value);
        setHideSearch(true);
        setSelectedRecord(list);
    };

    const clearSelection = () => {
        setSearchVal('');
        setHideSearch(false);
        setRadioButtonForSearch('');
    };

    return (
        <>
            <Modal show={isVisible} onClose={closePopupHandler} style={{ width: '70vw' }}>
                <ModalHeader>Create new Sub-Category to Map indicator</ModalHeader>
                <ModalBody>
                    <div className="add-and-move-add-sub-category-container">
                        <div className="add-and-move-add-sub-category-container__left-Container">
                            <Label text={ADD_NEW_SUB_CATEGORY_BANNER_MESSAGE}></Label>
                            {hideSearch === false && (
                                <InputWithSearchIcon
                                    placeholder="Search"
                                    value={searchVal}
                                    onChange={onSearchChangeHandler}
                                    hideClearButton={false}></InputWithSearchIcon>
                            )}
                            {searchVal.length > 0 ? (
                                hideSearch === false ? (
                                    [
                                        {
                                            id: 1,
                                            name: 'Population growth',
                                            path: ['Country essentials', 'Population'],
                                            entityType: 'Indicator'
                                        },
                                        {
                                            id: 2,
                                            name: 'Population median age',
                                            path: ['Country essentials', 'Population'],
                                            entityType: 'Indicator'
                                        },
                                        {
                                            id: 3,
                                            name: 'Population',
                                            path: ['Country essentials'],
                                            entityType: 'SubCategory',
                                            hasChild: true,
                                            hasIndicator: true
                                        }
                                    ].map((list: any, index: number) => {
                                        return (
                                            <SearchList
                                                radioButtonForSearch={radioButtonForSearch}
                                                handleChangeForSearch={handleChangeForSearch}
                                                key={index}
                                                searchVal={searchVal}
                                                data={list}
                                            />
                                        );
                                    })
                                ) : (
                                    <SearchSelection
                                        clearSelection={clearSelection}
                                        data={selectedRecord}
                                    />
                                )
                            ) : (
                                <HierarchyList
                                    isNoDataVisible={false}
                                    data={hierarchicalData}
                                    columnDefs={getHierarchyListColDefs(
                                        handleRadioClicked,
                                        toggleExpansion
                                    )}
                                />
                            )}
                        </div>
                        <div className="add-sub-category-container__right-Container">
                            <Label text="Fill in the below form"></Label>

                            <div className="subcategory-form">
                                <FormField className="subcategory-form-field">
                                    <Label text="Subcategory name (required)"></Label>
                                    <Input
                                        type={InputType.TEXT}
                                        value={name}
                                        onChange={handleNameChange}
                                    />
                                    <FieldErrorList
                                        field={CategoryField.Name}
                                        errors={fieldErrors}
                                    />
                                </FormField>
                                <FormField className="subcategory-form-field">
                                    <Label text="Sort Order (required)"></Label>
                                    <Select
                                        optionClassName="category-dropdown-option"
                                        value={sortOrder}
                                        options={sortOrderList}
                                        onChange={handleSortOrderChange}
                                    />
                                    <FieldErrorList
                                        field={CategoryField.SortOrder}
                                        errors={fieldErrors}
                                    />
                                </FormField>
                                <FormField className="subcategory-form-field">
                                    <Label text="Description"></Label>
                                    <TextArea value={description} onChange={handleDescription} />
                                    <FieldErrorList
                                        field={CategoryField.Description}
                                        errors={fieldErrors}
                                        data-testid="error"
                                    />
                                </FormField>
                            </div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexDirection: 'row',
                            width: '100%'
                        }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'left',
                                flexDirection: 'row',
                                width: '50%'
                            }}>
                            <Button
                                variant={ButtonVariant.Secondary}
                                onClick={() => onBackButtonClicked()}>
                                {ACTION_ITEMS.BACK_BUTTON}
                            </Button>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'right',
                                flexDirection: 'row',
                                width: '50%'
                            }}>
                            <Button size="medium" type="button" onClick={saveSubCategoryHandler}>
                                {ACTION_ITEMS.SAVE_BUTTON}
                            </Button>
                            <Button
                                size="medium"
                                variant="secondary"
                                type="button"
                                onClick={closePopupHandler}>
                                {ACTION_ITEMS.CANCEL_BUTTON}
                            </Button>
                        </div>
                    </div>

                    {/* <Button size="medium" type="button" onClick={saveSubCategoryHandler}>
                    Save
                </Button>
                <Button size="medium" variant="secondary" type="button" onClick={closePopupHandler}>
                    Cancel
                </Button> */}
                </ModalFooter>
            </Modal>
            {
                <MessageModal
                    headerName={'Warning'}
                    message={'Please choose one category or subcategory!'}
                    isVisible={isShowPopup}
                    isSecondaryButtonVisible={false}
                    type={MessageType.Warning}
                    primaryButton={ButtonLabel.Ok}
                    secondaryButton={ButtonLabel.Cancel}
                    onPrimaryAction={() => {
                        setShowPopup(false);
                    }}
                    onSecondaryAction={() => {
                        setShowPopup(false);
                    }}
                />
            }
        </>
    );
}
